import { ChevronDown, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Karte } from '../komponenten/Karte';
import { NochNichtsGepflegt } from '../komponenten/Zustaende';
import type { Inhalte, Phase } from '../directus/typen';

/**
 * Jahresplan: sechs Phasen als aufklappbare Liste.
 *
 * Die laufende Phase oeffnet sich automatisch, vergangene sind eingeklappt,
 * kommende lassen sich zur Vorschau oeffnen - wie im Prototyp.
 */
export function Jahresfahrplan({
  inhalte,
  phaseJetzt,
}: {
  inhalte: Inhalte;
  phaseJetzt: Phase | null;
}) {
  const [offenePhase, setOffenePhase] = useState<number | null>(phaseJetzt?.id ?? null);

  // Beim ersten Erkennen der laufenden Phase (z. B. sobald die Daten geladen
  // sind) automatisch aufklappen. Ein spaeteres manuelles Zuklappen der
  // Lehrkraft wird danach nicht mehr ueberschrieben.
  useEffect(() => {
    if (phaseJetzt !== null) setOffenePhase(phaseJetzt.id);
  }, [phaseJetzt]);

  if (inhalte.phasen.length === 0) {
    return (
      <NochNichtsGepflegt
        was="Zu den Phasen des Schuljahres"
        hinweis="Der Jahresfahrplan erscheint, sobald die Stufenleitung die Phasen angelegt hat."
      />
    );
  }

  return (
    <div className="space-y-2.5">
      {inhalte.phasen.map((p) => (
        <PhasenZeile
          key={p.id}
          phase={p}
          istJetzt={p.id === phaseJetzt?.id}
          istVergangen={phaseJetzt !== null && p.nummer < phaseJetzt.nummer}
          offen={offenePhase === p.id}
          wechsle={() => {
            setOffenePhase((vorher) => (vorher === p.id ? null : p.id));
          }}
        />
      ))}
    </div>
  );
}

function PhasenZeile({
  phase,
  istJetzt,
  istVergangen,
  offen,
  wechsle,
}: {
  phase: Phase;
  istJetzt: boolean;
  istVergangen: boolean;
  offen: boolean;
  wechsle: () => void;
}) {
  return (
    <Karte className={`overflow-hidden ${istJetzt ? 'ring-2 ring-marke-blau' : ''}`}>
      <button
        type="button"
        onClick={wechsle}
        aria-expanded={offen}
        className="flex w-full items-center gap-3 p-4 text-left"
        style={{ minHeight: 64 }}
      >
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold ${
            istJetzt
              ? 'bg-marke-blau text-white'
              : istVergangen
                ? 'bg-marke-flaeche text-marke-grau'
                : 'bg-marke-blau/10 text-marke-blau'
          }`}
        >
          {phase.nummer}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold text-marke-tinte">{phase.name}</span>
            {istJetzt && (
              <span className="rounded-full bg-ampel-plan/10 px-2 py-0.5 text-xs font-medium text-ampel-plan">
                aktuell
              </span>
            )}
          </div>
          {phase.zeitraum !== null && (
            <div className="mt-0.5 text-sm text-marke-grau">{phase.zeitraum}</div>
          )}
        </div>
        {offen ? (
          <ChevronDown size={20} className="shrink-0 text-marke-grau" aria-hidden="true" />
        ) : (
          <ChevronRight size={20} className="shrink-0 text-marke-grau" aria-hidden="true" />
        )}
      </button>

      {offen && (
        <div className="px-4 pb-4" style={{ paddingLeft: '4.25rem' }}>
          {phase.schwerpunkt !== null ? (
            <div className="rounded-xl bg-marke-flaeche p-3 text-sm text-marke-grau">
              {phase.schwerpunkt}
            </div>
          ) : (
            <div className="rounded-xl bg-marke-flaeche p-3 text-sm text-marke-grau italic">
              Kein Schwerpunkt hinterlegt.
            </div>
          )}
        </div>
      )}
    </Karte>
  );
}
